import {
  ENTITY,
  IncidentAlertAction,
  KitStatus,
  QueryKeysEnum,
  REPORT_STATUS,
  ReportOrderField,
  ReportTabKey,
  UserActions,
} from '@/enums';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import {
  CreateKitEntity,
  KitEntity,
  KitParams,
  MarkKitReceived,
  ReportReadyKitParams,
} from '@/pages/privatePages/kit/types';
import { QueryKeyTypes } from '@/queries/queryKeys';
import { passPageParams } from '@/queries/utils';
import { kitService } from '@/services/api-services';
import { CustomResponse } from '@/services/api-services/type';
import { ReportEntity } from '@/types';
import {
  InfiniteData,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { newQueryKeys } from './queryKeys';

type DefaultQuery = Partial<
  UseQueryOptions<
    CustomResponse<KitEntity[]>,
    Error,
    CustomResponse<KitEntity[]>,
    QueryKeyTypes<KitParams>
  >
>;

type DetailQuery = Partial<
  UseQueryOptions<KitEntity, Error, KitEntity, QueryKeyTypes>
>;

type SerialExistsQuery = Partial<
  UseQueryOptions<
    { exists: boolean },
    Error,
    { exists: boolean },
    QueryKeyTypes
  >
>;

type ReportReadyQuery = Partial<
  UseQueryOptions<
    CustomResponse<KitEntity[]>,
    Error,
    CustomResponse<KitEntity[]>,
    QueryKeyTypes<ReportReadyKitParams>
  >
>;

type ReportSupportKitParams = { action?: IncidentAlertAction };

type ReportSupportQuery = Partial<
  UseQueryOptions<
    KitEntity[],
    Error,
    KitEntity[],
    QueryKeyTypes<ReportSupportKitParams>
  >
>;

type InfiniteQuery = Partial<
  UseInfiniteQueryOptions<
    CustomResponse<KitEntity[]>,
    Error,
    InfiniteData<CustomResponse<KitEntity[]>>,
    CustomResponse<KitEntity[]>,
    QueryKeyTypes<KitParams>,
    number
  >
>;

type MutationCreate = UseMutationOptions<KitEntity, Error, CreateKitEntity>;

type MutationUpdate = UseMutationOptions<KitEntity, Error, Partial<KitEntity>>;

type MutationMassUpdate = UseMutationOptions<
  unknown,
  Error,
  { condition: Partial<KitEntity>; payload: Partial<KitEntity> }
>;

type MutationDelete = UseMutationOptions<unknown, Error, string>;

type MutationUpdateCustomerDetail = UseMutationOptions<
  unknown,
  Error,
  Parameters<typeof kitService.updateCustomerDetails>[0]
>;

type MutationMarkBulkKitReceived = UseMutationOptions<
  Record<string, string[]>,
  Error,
  MarkKitReceived
>;

export type BulkUpdateKitReportStatusPayload = {
  ids?: string[];
  notIncludeIds?: string[];
  status: KitStatus;
  type: ReportTabKey;
  publishStatus?: REPORT_STATUS[];
  projectId?: string;
  channelId?: string;
  qcStatus?: string;
  orderId?: string;
  batchIds?: string[];
  isFetchAll?: boolean;
  orderField?: ReportOrderField;
};

type MutationBulkUpdateKitReportStatus = UseMutationOptions<
  any,
  Error,
  BulkUpdateKitReportStatusPayload,
  unknown
>;

const useGetKits = (props: { params?: KitParams; options?: DefaultQuery }) => {
  const { params = {}, options = {} } = props ?? {};
  const { canGetKits } = useCheckScopeCombinations(
    [[{ entity: ENTITY.KITS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useQuery({
    queryKey: newQueryKeys.kit.list(passPageParams(params)),
    queryFn: kitService.get,
    placeholderData: previousData => previousData,
    ...options,
    enabled: (options.enabled ?? true) && canGetKits,
  });

  return query;
};

const useGetKitsForReTriggerPdf = (props: {
  params?: KitParams;
  options?: DefaultQuery;
}) => {
  const { params = {}, options = {} } = props ?? {};
  const { canGetKits } = useCheckScopeCombinations(
    [[{ entity: ENTITY.KITS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useQuery({
    queryKey: newQueryKeys.kit.list(passPageParams(params)),
    queryFn: () => kitService.getKitForReTriggerPdfGeneration(params),
    ...options,
    enabled: (options.enabled ?? true) && canGetKits,
  });

  return query;
};

const useGetInfiniteKit = (props: {
  params?: KitParams;
  options?: InfiniteQuery;
}) => {
  const { params = {}, options = {} } = props ?? {};
  const { canGetKits } = useCheckScopeCombinations(
    [[{ entity: ENTITY.KITS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useInfiniteQuery({
    queryKey: newQueryKeys.kit.list(passPageParams(params)),
    queryFn: kitService.getInfinite,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    ...options,
    enabled: (options.enabled ?? true) && canGetKits,
  });

  return query;
};

const useGetKitDetail = (props: {
  params: KitParams;
  options?: DetailQuery;
}) => {
  const { params, options = {} } = props ?? {};
  const { canGetKits } = useCheckScopeCombinations(
    [[{ entity: ENTITY.KITS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useQuery({
    queryKey: newQueryKeys.kit.detail(params?.serial ?? '', params),
    queryFn: () => kitService.getKitDetail(params),
    ...options,
    enabled: (options.enabled ?? true) && canGetKits,
  });

  return query;
};

// Shared by the hook below and imperative `queryClient.fetchQuery` callers
// (e.g. pre-submit validation) so both hit the same cache entry.
export const kitSerialExistsQueryOptions = (serial: string) => ({
  queryKey: newQueryKeys.kit.detail(serial, { exists: true }),
  queryFn: () => kitService.checkSerialExists(serial),
  staleTime: 30 * 1000,
});

const useCheckKitSerialExists = (props: {
  serial: string;
  options?: SerialExistsQuery;
}) => {
  const { serial, options = {} } = props ?? {};

  const query = useQuery({
    ...kitSerialExistsQueryOptions(serial),
    ...options,
    enabled: (options.enabled ?? true) && !!serial,
  });

  return query;
};

const useReportReadyKits = (
  params: ReportReadyKitParams = {},
  options: ReportReadyQuery = {},
) => {
  const query = useQuery({
    queryKey: newQueryKeys.kit.detail(
      'report-ready',
      passPageParams(params),
    ) as QueryKeyTypes<ReportReadyKitParams>,
    queryFn: () => kitService.getReportReadyKits(params),
    placeholderData: previousData => previousData,
    ...options,
  });

  return query;
};

const useReportSupportKits = (
  params: ReportSupportKitParams = {},
  options: ReportSupportQuery = {},
) => {
  const query = useQuery({
    queryKey: newQueryKeys.kit.detail(
      'report-support',
      params,
    ) as QueryKeyTypes<ReportSupportKitParams>,
    queryFn: () => kitService.getReportSupportKits(params),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

  return query;
};

const useCreateKit = (options: MutationCreate = {}) => {
  const mutation = useMutation({
    mutationFn: kitService.create,
    mutationKey: newQueryKeys.kit.invalidateListsKey(),
    ...options,
  });

  return mutation;
};

const useUpdateKit = (options: MutationUpdate = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: kitService.update,
    mutationKey: newQueryKeys.kit.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      const { id = '' } = variables;
      onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.kit.detail(id),
      });
    },
    ...rest,
  });

  return mutation;
};

export const useMassUpdateKit = (options: MutationMassUpdate = {}) => {
  const mutation = useMutation({
    mutationFn: kitService.massUpdate,
    mutationKey: newQueryKeys.kit.invalidateListsKey(),
    ...options,
  });

  return mutation;
};

type MutationValidateSourceKit = UseMutationOptions<
  Awaited<ReturnType<typeof kitService.validateSourceKit>>,
  Error,
  string
>;

// Kit ID check for products requiring a source kit (e.g. Optimise Add-on).
// A mutation rather than a query: it runs on demand (blur / Enter) for the
// serial the user typed.
const useValidateSourceKitMutation = (
  options: MutationValidateSourceKit = {},
) => {
  const mutation = useMutation({
    mutationFn: kitService.validateSourceKit,
    ...options,
  });

  return mutation;
};

// "Update customer details" on the Kit page: assigns a patient to a kit,
// then refreshes the kit lists (they show the assigned patient).
const useUpdateCustomerDetailMutation = (
  options: MutationUpdateCustomerDetail = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: kitService.updateCustomerDetails,
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.kit.invalidateListsKey(),
      });
    },
    ...rest,
  });

  return mutation;
};

const useDeleteKit = (options: MutationDelete = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: kitService.delete,
    mutationKey: newQueryKeys.kit.invalidateListsKey(),
    onSuccess: (data, id, context) => {
      onSuccess?.(data, id, context);
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.kit.detail(id),
      });
    },
    ...rest,
  });

  return mutation;
};

const useMarkBulkKitReceived = (options: MutationMarkBulkKitReceived = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: kitService.markBulkKitReceived,
    mutationKey: newQueryKeys.kit.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
      variables.kitSerials.map(serial =>
        queryClient.invalidateQueries({
          queryKey: newQueryKeys.kit.detail(serial),
        }),
      );
    },
    ...rest,
  });

  return mutation;
};

type MarkKitsClinicReceivedMutation = UseMutationOptions<
  void,
  Error,
  { orderIds: string[]; kitIds: string[] }
>;

type BulkUpdateAllowDnaExtractionMutation = UseMutationOptions<
  void,
  Error,
  { serial: string; allowDnaExtraction: boolean }[]
>;

type SetKitReportStatusMutation = UseMutationOptions<
  void,
  Error,
  { serial: string; status: KitStatus }
>;

export const useMarkKitsClinicReceived = (
  options: MarkKitsClinicReceivedMutation = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: kitService.markKitsClinicReceived,
    mutationKey: newQueryKeys.kit.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
      variables.kitIds.map(id =>
        queryClient.invalidateQueries({
          queryKey: newQueryKeys.kit.detail(id),
        }),
      );
    },
    ...rest,
  });

  return mutation;
};

// export const useGetKitSamples = (props: {
//   serial: string;
//   options: SampleQuery;
// }) => {
//   const { serial, options = {} } = props ?? {};

//   const query = useQuery({
//     queryKey: newQueryKeys.sample.,
//     queryFn: kitService.get,
//     ...options,
//   });

//   return query;
// };

const useBulkUpdateAllowDnaExtractionMutation = (
  options: BulkUpdateAllowDnaExtractionMutation = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: kitService.bulkUpdateAllowDnaExtraction,
    mutationKey: newQueryKeys.kit.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
      variables.map(({ serial }) =>
        queryClient.invalidateQueries({
          queryKey: newQueryKeys.kit.detail(serial),
        }),
      );
    },
    ...rest,
  });

  return mutation;
};

const useSetKitReportStatusMutation = (
  options: SetKitReportStatusMutation = {},
) => {
  const _queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: kitService.setKitReportStatus,
    mutationKey: newQueryKeys.kit.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
    ...rest,
  });

  return mutation;
};

const getInvalidateKeyAndScopeKeyBaseOnType = (
  type: ReportTabKey,
): {
  invalidateKey: QueryKeyTypes;
  scopeKey: string;
} => {
  switch (type) {
    case ReportTabKey.SIXTEEN_S:
      return {
        invalidateKey: newQueryKeys.report.invalidateListsKey(),
        scopeKey: QueryKeysEnum.REPORT,
      };
    case ReportTabKey.ANALYSIS:
      return {
        invalidateKey: newQueryKeys.analysisReport.invalidateListsKey(),
        scopeKey: QueryKeysEnum.ANALYSIS_REPORT,
      };
    default:
      return {
        invalidateKey: newQueryKeys.report.invalidateListsKey(),
        scopeKey: QueryKeysEnum.REPORT,
      };
  }
};

type MutationContext = {
  snapshots: Array<{ queryKey: any; data: any }>;
  type: ReportTabKey;
};
const useBulkUpdateKitReportStatusMutation = (
  options: MutationBulkUpdateKitReportStatus = {},
  isOptimistic: boolean = false,
) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, onMutate, ...rest } = options;

  const mutationOptions: UseMutationOptions<
    any,
    Error,
    BulkUpdateKitReportStatusPayload,
    MutationContext
  > = {
    onMutate: async (payload: BulkUpdateKitReportStatusPayload) => {
      const { ids = [], notIncludeIds = [], status, type } = payload;

      //Define invalidate key, scope key base on report tab key.
      const { invalidateKey, scopeKey } =
        getInvalidateKeyAndScopeKeyBaseOnType(type);

      if (!isOptimistic) {
        queryClient.invalidateQueries({
          queryKey: invalidateKey,
        });
        return;
      }

      if (isOptimistic) {
        await queryClient.cancelQueries({
          queryKey: invalidateKey,
        });

        const queryKey = [{ scope: scopeKey, entity: 'lists' }];

        // Get all matching queries
        const queriesCacheReport = queryClient.getQueriesData({
          queryKey,
          exact: false,
        });

        // Store snapshots for rollback
        const snapshots: Array<{ queryKey: any; data: any }> = [];

        // Update each query individually while preserving structure
        queriesCacheReport.forEach(([key, queryData]) => {
          // Save snapshot
          snapshots.push({ queryKey: key, data: queryData });

          // Update the data within this query's structure
          if (queryData) {
            const updatedQueryData = updateQueryDataReportStatus(
              queryData,
              status,
              ids,
              notIncludeIds,
            );

            // Set the updated data for this specific query
            queryClient.setQueryData(key, updatedQueryData);
          }
        });

        return {
          snapshots,
          type,
        };
      }
      return undefined;
    },

    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },

    onError: (err, updated, context: any) => {
      onError?.(err, updated, context);
      // Rollback all queries
      if (isOptimistic && context) {
        context?.snapshots?.forEach(({ queryKey, data }: any) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },

    onSettled: async (
      data,
      error,
      payload: BulkUpdateKitReportStatusPayload,
    ) => {
      const { invalidateKey } = getInvalidateKeyAndScopeKeyBaseOnType(
        payload?.type,
      );

      if (isOptimistic) {
        await queryClient.invalidateQueries({
          queryKey: invalidateKey,
        });
      }
    },
  };

  return useMutation({
    mutationFn: kitService.bulkUpdateKitReportStatus,
    mutationKey: newQueryKeys.kit.invalidateListsKey(),
    ...mutationOptions,
    ...rest,
  });
};

// Helper function that preserves the query data structure
function updateQueryDataReportStatus(
  queryData: any,
  status: KitStatus,
  ids: string[],
  notIncludeIds: string[],
) {
  // Handle different structures
  if (!queryData) return queryData;

  // If queryData has a 'data' property (paginated response)
  if (queryData.data && Array.isArray(queryData.data)) {
    return {
      ...queryData,
      data: queryData.data.map((item: ReportEntity) => {
        if (notIncludeIds.includes(item.id)) return item;

        if (!ids?.length || ids?.includes(item?.id)) {
          return {
            ...item,
            currentStatus: {
              ...item.currentStatus,
              status,
              updatedAt: new Date(),
              createdAt: new Date(),
            },
          };
        }

        // if (ids.includes(item.id)) {
        //   return {
        //     ...item,
        //     currentStatus: {
        //       ...item.currentStatus,
        //       status,
        //       updatedAt: new Date(),
        //       createdAt: new Date(),
        //     },
        //   };
        // }

        return item;
      }),
    };
  }

  // If queryData is directly an array
  if (Array.isArray(queryData)) {
    return queryData.map((item: ReportEntity) => {
      if (notIncludeIds.includes(item.id)) return item;

      if (!ids?.length || ids?.includes(item?.id)) {
        return {
          ...item,
          currentStatus: {
            ...item.currentStatus,
            status,
            updatedAt: new Date(),
            createdAt: new Date(),
          },
        };
      }

      // if (ids.includes(item.id)) {
      //   return {
      //     ...item,
      //     currentStatus: {
      //       ...item.currentStatus,
      //       status,
      //       createdAt: new Date(),
      //       updatedAt: new Date(),
      //     },
      //   };
      // }

      return item;
    });
  }

  return queryData;
}
export {
  useBulkUpdateAllowDnaExtractionMutation,
  useBulkUpdateKitReportStatusMutation,
  useCheckKitSerialExists,
  useCreateKit,
  useDeleteKit,
  useGetInfiniteKit,
  useGetKitDetail,
  useGetKits,
  useGetKitsForReTriggerPdf,
  useMarkBulkKitReceived,
  useReportReadyKits,
  useReportSupportKits,
  useSetKitReportStatusMutation,
  useUpdateCustomerDetailMutation,
  useUpdateKit,
  useValidateSourceKitMutation,
};
